import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LegalHeader from '@/components/legal/legal-header';
import LegalLink from '@/components/legal/legal-link';
import { useTranslations } from '@/i18n/use-translations';

type TokenPayload = {
  email: string;
  organizationId: string;
  organizationName?: string;
  action: 'opt-out' | 'opt-in';
};

type ProcessResult = {
  action: 'opt-out' | 'opt-in';
  email: string;
  organizationName: string;
};

type PageState =
  | { status: 'loading' }
  | { status: 'confirm'; payload: TokenPayload }
  | { status: 'processing' }
  | { status: 'success'; result: ProcessResult }
  | { status: 'error'; message: string };

function ConfirmStep({
  payload,
  organizationLabel,
  onConfirm,
  t,
}: {
  payload: TokenPayload;
  organizationLabel: (name?: string) => string;
  onConfirm: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const organization = organizationLabel(payload.organizationName);
  const isOptOut = payload.action === 'opt-out';

  const consequenceItems = isOptOut
    ? [
        t('emailPreferences.optOutPage.optOutConsequenceInvitations'),
        t('emailPreferences.optOutPage.optOutConsequenceReminders'),
        t('emailPreferences.optOutPage.optOutConsequenceUpdates'),
      ]
    : [
        t('emailPreferences.optOutPage.optInConsequenceInvitations'),
        t('emailPreferences.optOutPage.optInConsequenceReminders'),
        t('emailPreferences.optOutPage.optInConsequenceUpdates'),
      ];

  return (
    <>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {isOptOut
          ? t('emailPreferences.optOutPage.optOutTitle', { organization })
          : t('emailPreferences.optOutPage.optInTitle', { organization })}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {isOptOut
          ? t('emailPreferences.optOutPage.optOutDescription', { email: payload.email, organization })
          : t('emailPreferences.optOutPage.optInDescription', { email: payload.email, organization })}
      </Typography>
      <Box sx={{ mb: 3, textAlign: 'left' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {isOptOut
            ? t('emailPreferences.optOutPage.optOutConsequencesHeading', { organization })
            : t('emailPreferences.optOutPage.optInConsequencesHeading', { organization })}
        </Typography>
        <ConsequencesList items={consequenceItems} />
      </Box>
      <Button variant="contained" onClick={onConfirm} size="large">
        {isOptOut ? t('emailPreferences.optOutPage.confirmOptOut') : t('emailPreferences.optOutPage.confirmOptIn')}
      </Button>
    </>
  );
}

function ConsequencesList({ items }: { items: string[] }) {
  return (
    <Box
      component="ul"
      sx={{
        'm': 0,
        'pl': 2.5,
        'textAlign': 'left',
        '& li': { mb: 1 },
        '& li:last-child': { mb: 0 },
      }}
    >
      {items.map((item, index) => (
        <Typography key={index} component="li" variant="body2" color="text.secondary">
          {item}
        </Typography>
      ))}
    </Box>
  );
}

export default function EmailOptOutPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const t = useTranslations();

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: t('emailPreferences.optOutPage.noToken') });
      return;
    }

    const abort = new AbortController();
    let cancelled = false;

    const verify = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/email-preferences/verify-token?token=${encodeURIComponent(token)}`,
          { cache: 'no-store', signal: abort.signal },
        );
        if (res.status === 429) {
          throw new Error('rate-limited');
        }
        if (!res.ok) throw new Error('Invalid or expired token');
        const payload: TokenPayload = await res.json();
        if (!cancelled) {
          setState({ status: 'confirm', payload });
        }
      } catch (error) {
        if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) {
          return;
        }
        setState({
          status: 'error',
          message:
            error instanceof Error && error.message === 'rate-limited'
              ? t('emailPreferences.optOutPage.rateLimited')
              : t('emailPreferences.optOutPage.invalidToken'),
        });
      }
    };

    void verify();
    return () => {
      cancelled = true;
      abort.abort();
    };
    // Intentionally only [token]: `t` from useTranslations is a new function each render and would retrigger this effect in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verify once per token in the URL
  }, [token]);

  const organizationLabel = (name?: string) => name?.trim() || t('emailPreferences.optOutPage.fallbackOrganization');

  const handleConfirm = async () => {
    if (!token) return;
    setState({ status: 'processing' });

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/email-preferences/token`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (res.status === 429) {
        throw new Error('rate-limited');
      }
      if (!res.ok) throw new Error('Failed to process request');
      const result: ProcessResult = await res.json();
      setState({ status: 'success', result });
    } catch (error) {
      setState({
        status: 'error',
        message:
          error instanceof Error && error.message === 'rate-limited'
            ? t('emailPreferences.optOutPage.rateLimited')
            : t('emailPreferences.optOutPage.genericError'),
      });
    }
  };

  return (
    <>
      <LegalHeader />
      <Box sx={{ pt: { xs: '50px', md: '64px' } }}>
        <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
          <Paper sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
            {state.status === 'loading' && <CircularProgress />}

            {state.status === 'confirm' && (
              <ConfirmStep
                payload={state.payload}
                organizationLabel={organizationLabel}
                onConfirm={handleConfirm}
                t={t}
              />
            )}

            {state.status === 'processing' && <CircularProgress />}

            {state.status === 'success' && (
              <>
                <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {state.result.action === 'opt-out'
                    ? t('emailPreferences.optOutPage.successOptOut')
                    : t('emailPreferences.optOutPage.successOptIn')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {state.result.action === 'opt-out'
                    ? t('emailPreferences.optOutPage.successOptOutDescription', {
                        organization: state.result.organizationName,
                        email: state.result.email,
                      })
                    : t('emailPreferences.optOutPage.successOptInDescription', {
                        organization: state.result.organizationName,
                        email: state.result.email,
                      })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {t('emailPreferences.optOutPage.managePreferences')}
                </Typography>
              </>
            )}

            {state.status === 'error' && (
              <>
                <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {t('emailPreferences.optOutPage.errorTitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {state.message}
                </Typography>
              </>
            )}
          </Paper>
        </Container>
      </Box>
      <LegalLink />
    </>
  );
}
